import { createRef, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import MainContent from "../../../layouts/maincontent/MainContent";
import SearchSelectElement from "../../../shared/form/SearchSelectElement";
import InputElement from "../../../shared/form/InputElement";
import {
  getStaticGovernorate,
  getStaticPoliceStations,
  getUnifiedDeclarationAllLookups,
  getPaymentReasons,
} from "../../../adapters/apis/lookups/lookups";
import { getWalletDetails } from "../../../adapters/apis/wallet/wallet";
import stylesRequestData from "../../../components/UnifiedTaxDeclaration/UnifiedTaxDeclarationRequestData/index.module.css";
import styleAddNewTaxDeclaration from "../AddUnifiedTaxDeclaration/AddNewTaxDeclaration.module.css";

import styleData from "./createPropertyPaymentRequest.module.css";
import { get, post, put } from "../../../adapters/xhr/api";

const PROPERTY_TYPE_IDS_REQUIRING_FLOOR = [1, 2, 3, 4, 11];
const PROPERTY_TYPE_IDS_REQUIRING_UNIT_NUMBER = [1, 2, 3, 4];

const GENERAL_TEXT_PATTERN =
  /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9.,،\/\-\s]+$/;

const EXTENDED_TEXT_PATTERN =
  /^[A-Za-z0-9\u0621-\u063A\u0641-\u064A._,،+\/\\\-\s]+$/;

const UNIT_TEXT_PATTERN = /^[A-Za-z0-9\u0621-\u063A\u0641-\u064A\s._,\-\\/،]*$/;

const NATIONAL_PROPERTY_ID_PATTERN = /^[0-9-]{0,30}$/;
const AMOUNT_TYPING_PATTERN = /^(?!0+(?:\.0{0,2})?$)\d{1,19}(?:\.\d{0,2})?$/;
const AMOUNT_FINAL_PATTERN = /^(?!0+(?:\.0{0,2})?$)\d{1,19}(?:\.\d{0,2})?$/;

const toSelectOptions = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    value: item?.id ?? item?.value,
    label: item?.name ?? item?.label ?? "",
    raw: item,
  }));

const getApiList = (response) => response?.data?.data || [];

const getBackendErrorMessages = (error, fallback) => {
  const fileErrors = error?.response?.data?.errors?.file;

  if (Array.isArray(fileErrors) && fileErrors.length > 0) {
    return fileErrors;
  }

  return [error?.response?.data?.message || fallback];
};

const initialForm = {
  property_type_id: null,
  governorate_id: null,
  police_station_id: null,
  city: "",
  district: "",
  village: "",
  mogawra: "",
  street: "",
  settlement: "",
  feature_number: "",
  national_property_id: "",
  declaration_floor_id: null,
  other_floor_number: "",
  unit_number: "",
  payment_reason_id: null,
  other_payment_reason: "",
  amount: "",
  pay_from_wallet: false,
  has_debt_docs: null,
};

const getEmptyDocumentRow = () => ({
  id: Math.random(),
  document_name: "",
  file: null,
  path: null,
  original_file_name: null,
  file_error: "",
});

function CreatePropertyPaymentRequest() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);

  const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);
  const [governorateOptions, setGovernorateOptions] = useState([]);
  const [policeStationOptions, setPoliceStationOptions] = useState([]);
  const [floorOptions, setFloorOptions] = useState([]);
  const [hasDocsOptions, setHasDocsOptions] = useState([]);

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [documentRows, setDocumentRows] = useState([getEmptyDocumentRow()]);
  const documentFileInputRef = useRef({});
  const [inputKeys, setInputKeys] = useState({});
  const [submitInfo, setSubmitInfo] = useState("");
  const [backendError, setBackendError] = useState("");
  const [paymentReasons, setPaymentReasonsOptions] = useState([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  const isFloorRequired = PROPERTY_TYPE_IDS_REQUIRING_FLOOR.includes(
    Number(form.property_type_id),
  );

  const isUnitNumberRequired = PROPERTY_TYPE_IDS_REQUIRING_UNIT_NUMBER.includes(
    Number(form.property_type_id),
  );

  const isOtherFloorSelected = parseInt(form.declaration_floor_id) === -1;
  const isOtherPaymentReasonSelected = parseInt(form.payment_reason_id) === -1;
  const shouldShowDocsSection = parseInt(form.has_debt_docs) === 1;

  useEffect(() => {
    const loadLookups = async () => {
      setIsLoading(true);
      setLookupError("");
      try {
        const [allLookupsRes, governoratesRes, paymentReasons] =
          await Promise.all([
            getUnifiedDeclarationAllLookups(
              "api/declaration-system/declaration-lookups/declaration-all-lookups",
            ),
            getStaticGovernorate(),
            getPaymentReasons(),
          ]);

        const lookupsData = getApiList(allLookupsRes);

        const propertyTypes =
          lookupsData?.property_types || lookupsData?.propertyTypes || [];
        const docsAnswerOptions =
          lookupsData?.DoYouWantExtraAttachmentsForRequestTypes || [];
        const floorTypes = lookupsData?.real_estate_floors || [];

        setPropertyTypeOptions(toSelectOptions(propertyTypes));
        setGovernorateOptions(toSelectOptions(getApiList(governoratesRes)));
        setHasDocsOptions(toSelectOptions(docsAnswerOptions));
        setPaymentReasonsOptions(toSelectOptions(getApiList(paymentReasons)));
        setFloorOptions(toSelectOptions(floorTypes));
        getWalletDetails()
          .then((response) => {
            const wallet = response?.data?.data || {};
            setWalletBalance({
              balance: wallet?.balance || "0.00",
              walletTransaction: wallet?.walletTransaction || [],
            });
          })
          .catch(() => {
            console.log("حدث خطأ أثناء تحميل بيانات المحفظة");
          })
          .finally(() => {
            setIsLoading(false);
          });
      } catch (error) {
        setLookupError("تعذر تحميل القوائم. برجاء المحاولة مرة أخرى.");
      } finally {
        setIsLoading(false);
      }
    };

    loadLookups();
  }, []);

  useEffect(() => {
    if (!id) return;

    const loadUnit = async () => {
      try {
        setIsLoading(true);
        const response = await get(
          `api/declaration-system/my-debts/unit/${id}`,
        );
        const unit = response?.data?.unit || {};

        setForm({
          property_type_id: unit?.property_type_id ?? null,
          governorate_id: unit?.governorate_id ?? null,
          police_station_id: unit?.police_station_id ?? null,
          city: unit?.city || "",
          district: unit?.district || "",
          village: unit?.village || "",
          mogawra: unit?.mogawra || "",
          street: unit?.street || "",
          settlement: unit?.settlement || "",
          feature_number: unit?.feature_number || "",
          national_property_id: unit?.national_property_id || "",
          declaration_floor_id: unit?.declaration_floor_id ?? null,
          other_floor_number: unit?.other_floor_number || "",
          unit_number: unit?.unit_number || "",
          payment_reason_id: unit?.payment_reason_id ?? null,
          other_payment_reason: unit?.other_payment_reason || "",
          amount: unit?.amount ?? "",
          pay_from_wallet: Boolean(unit?.pay_from_wallet),
          has_debt_docs: unit?.has_debt_docs ?? null,
        });

        const existingDocs =
          unit?.attachments ||
          unit?.my_debt_supporting_documents ||
          unit?.debt_supporting_document ||
          [];

        if (existingDocs.length > 0) {
          setDocumentRows(
            existingDocs.map((doc) => ({
              id: doc?.id ?? Math.random(),
              document_name: doc?.name || doc?.document_name || "",
              file: null,
              path: doc?.path || null,
              original_file_name: doc?.original_file_name || null,
              file_error: "",
            })),
          );
        }
      } catch (error) {
        setLookupError("تعذر تحميل بيانات الوحدة. برجاء المحاولة مرة أخرى.");
      } finally {
        setIsLoading(false);
      }
    };

    loadUnit();
  }, [id]);

  useEffect(() => {
    const loadPoliceStations = async () => {
      if (!form.governorate_id) {
        setPoliceStationOptions([]);
        setForm((prev) => ({ ...prev, police_station_id: null }));
        return;
      }

      try {
        const response = await getStaticPoliceStations(form.governorate_id);
        setPoliceStationOptions(toSelectOptions(getApiList(response)));
      } catch (error) {
        setPoliceStationOptions([]);
      }
    };

    loadPoliceStations();
  }, [form.governorate_id]);

  useEffect(() => {
    (documentRows || []).forEach((document) => {
      if (!documentFileInputRef.current[document.id]) {
        documentFileInputRef.current[document.id] = createRef();
      }
    });
  }, [documentRows]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setSubmitInfo("");
    setBackendError("");
  };

  const handleInputElementChange = (
    _parentCompenent,
    _compenent,
    name,
    value,
  ) => {
    setField(name, value);
  };

  const handleAmountInputChange = (
    _parentCompenent,
    _compenent,
    name,
    value,
  ) => {
    if (value === "" || AMOUNT_TYPING_PATTERN.test(value)) {
      setField(name, value);
    }
  };

  const buildRequestBody = () => ({
    property_type_id: Number(form.property_type_id),

    governorate_id: Number(form.governorate_id),

    police_station_id: Number(form.police_station_id),

    city: form.city?.trim() || null,
    district: form.district?.trim() || null,
    village: form.village?.trim() || null,
    mogawra: form.mogawra?.trim() || null,
    street: form.street?.trim() || null,
    settlement: form.settlement?.trim() || null,

    feature_number: form.feature_number?.trim(),

    national_property_id: form.national_property_id?.trim() || null,

    declaration_floor_id: form.declaration_floor_id,
    other_floor_number: isOtherFloorSelected
      ? form.other_floor_number?.trim()
      : null,

    unit_number: isUnitNumberRequired ? form.unit_number?.trim() : null,

    payment_reason_id: Number(form.payment_reason_id),
    other_payment_reason: isOtherPaymentReasonSelected
      ? form.other_payment_reason?.trim()
      : null,

    amount: form.amount,

    pay_from_wallet: form.pay_from_wallet,

    has_debt_docs: Number(form.has_debt_docs),

    ...(Number(form.has_debt_docs) === 1 && {
      my_debt_supporting_documents: documentRows.map((doc) => ({
        name: doc.document_name,
        path: doc.path,
        original_file_name: doc.original_file_name,
      })),
    }),
  });
  const handleSelectChange = (_parentCompenent, _compenent, name, value) => {
    setField(name, value);

    if (name === "property_type_id") {
      setField("declaration_floor_id", null);
      setField("other_floor_number", "");
      setField("unit_number", "");
    }

    if (name === "governorate_id") {
      setField("police_station_id", null);
    }

    if (name === "declaration_floor_id" && parseInt(value) !== -1) {
      setField("other_floor_number", "");
    }

    if (name === "payment_reason_id" && parseInt(value) !== -1) {
      setField("other_payment_reason", "");
    }
  };

  const handleDocumentNameChange = (documentId, value) => {
    if (
      value.length > 100 ||
      (value !== "" && !UNIT_TEXT_PATTERN.test(value))
    ) {
      return;
    }

    setErrors((prev) => {
      const updatedErrors = { ...prev };
      const documentIndex = documentRows.findIndex(
        (row) => row.id === documentId,
      );

      delete updatedErrors[`document_name_${documentIndex}`];
      return updatedErrors;
    });
    setBackendError("");

    setDocumentRows((prev) =>
      prev.map((row) =>
        row.id === documentId ? { ...row, document_name: value } : row,
      ),
    );
    setSubmitInfo("");
  };

  const validateFile = (file) => {
    if (!file) {
      return "يجب رفع الملف";
    }

    return "";
  };

  const handleDocumentFileChange = async (documentId, selectedFile) => {
    const fileError = validateFile(selectedFile);

    setErrors((prev) => {
      const updatedErrors = { ...prev };
      const documentIndex = documentRows.findIndex(
        (row) => row.id === documentId,
      );

      delete updatedErrors[`document_file_${documentIndex}`];
      return updatedErrors;
    });
    setBackendError("");

    setDocumentRows((prev) =>
      prev.map((row) =>
        row.id === documentId
          ? {
              ...row,
              file: fileError ? null : selectedFile,
              path: null,
              original_file_name: null,
              file_error: fileError,
            }
          : row,
      ),
    );

    setSubmitInfo("");

    if (fileError) {
      return;
    }

    try {
      setIsUploadingDocuments(true);

      const data = new FormData();
      data.append("label", "supporting_documents");
      data.append("file", selectedFile);

      const response = await post(
        "api/declaration-system/declarations/upload-attachments",
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
        },
        data,
      );

      const uploadedDocument = response?.data?.data;

      if (uploadedDocument?.path) {
        setBackendError("");
      }

      setDocumentRows((prev) =>
        prev.map((row) =>
          row.id === documentId
            ? {
                ...row,
                path: uploadedDocument?.path || null,
                original_file_name: uploadedDocument?.original_file_name,
                file_error: uploadedDocument?.path ? "" : "يجب رفع الصورة",
              }
            : row,
        ),
      );
    } catch (error) {
      setBackendError(getBackendErrorMessages(error, "يجب رفع الصورة"));
      window.scrollTo(0, 0);
      setDocumentRows((prev) =>
        prev.map((row) =>
          row.id === documentId
            ? {
                ...row,
                file: null,
                path: null,
                original_file_name: null,
                file_error: "يجب رفع الصورة",
              }
            : row,
        ),
      );
    } finally {
      setIsUploadingDocuments(false);
    }
  };

  const handleRemoveDocumentFile = (documentId) => {
    setInputKeys((prev) => ({
      ...prev,
      [documentId]: (prev[documentId] || 0) + 1,
    }));

    setDocumentRows((prev) =>
      prev.map((row) =>
        row.id === documentId
          ? {
              ...row,
              file: null,
              path: null,
              original_file_name: null,
              file_error: "",
            }
          : row,
      ),
    );

    const inputElement = documentFileInputRef?.current[documentId]?.current;
    if (inputElement instanceof HTMLInputElement) {
      inputElement.value = null;
    }

    setSubmitInfo("");
    setBackendError("");
  };

  const addDocumentRow = () => {
    const nextErrors = {};

    if (documentRows.length >= 5) {
      return;
    }

    validateFileSubmit(nextErrors);

    if (!(Object.keys(nextErrors).length === 0)) {
      setErrors((prev) => ({
        ...prev,
        ...nextErrors,
      }));

      return;
    }

    setErrors((prev) => {
      const updated = { ...prev };

      Object.keys(updated).forEach((key) => {
        if (
          key.startsWith("document_file_") ||
          key.startsWith("document_name_")
        ) {
          delete updated[key];
        }
      });

      return updated;
    });

    setDocumentRows((prev) => [...prev, getEmptyDocumentRow()]);
    setSubmitInfo("");
  };

  const removeDocumentRow = (documentId) => {
    if (documentRows.length <= 1) {
      return;
    }

    setDocumentRows((prev) => prev.filter((row) => row.id !== documentId));
    setSubmitInfo("");
  };

  const validateFileSubmit = (nextErrors) => {
    if (shouldShowDocsSection) {
      documentRows.forEach((row, index) => {
        if (!row.document_name?.trim()) {
          nextErrors[`document_name_${index}`] = "يجب ادخال اسم المستند";
        }

        const fileError =
          row.file_error ||
          (!row.file && !row.path && validateFile(row.file)) ||
          (row.file && !row.path && "برجاء انتظار رفع الملف");
        if (fileError) {
          nextErrors[`document_file_${index}`] = fileError;
        }
      });
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.property_type_id)
      nextErrors.property_type_id = "يجب اختيار نوع العقار";
    if (!form.governorate_id) nextErrors.governorate_id = "يجب إختيار المحافظة";
    if (!form.police_station_id)
      nextErrors.police_station_id = "يجب إختيار قسم/مركز الشرطة";
    if (!form.feature_number?.trim())
      nextErrors.feature_number =
        "يجب إدخال رقم العقار/المبنى كما هو متعارف عليه";

    if (isFloorRequired) {
      if (!form.declaration_floor_id)
        nextErrors.declaration_floor_id = "يجب اختيار دور";
      if (isOtherFloorSelected && !form.other_floor_number?.trim()) {
        nextErrors.other_floor_number = "يجب ادخال دور آخر";
      }
    }

    if (isUnitNumberRequired && !form.unit_number?.trim()) {
      nextErrors.unit_number = "يجب ادخال رقم الوحدة";
    }

    if (!form.payment_reason_id)
      nextErrors.payment_reason_id = "يجب اختيار سبب السداد";
    if (isOtherPaymentReasonSelected && !form.other_payment_reason?.trim()) {
      nextErrors.other_payment_reason = "يجب ادخال سبب سداد الآخر";
    }

    if (!form.amount || !AMOUNT_FINAL_PATTERN.test(form.amount)) {
      nextErrors.amount = "يجب ادخال المبلغ المطلوب سداده";
    }

    if (!form.has_debt_docs) {
      nextErrors.has_debt_docs = "يجب اختيار إجابة";
    }

    validateFileSubmit(nextErrors);

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isUploadingDocuments) {
      setSubmitInfo("برجاء الانتظار حتى يكتمل رفع المستندات");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      setSubmitInfo("");
      setBackendError("");

      const payload = buildRequestBody();

      if (isEditMode) {
        await put(`api/declaration-system/my-debts/unit/${id}`, {}, payload);
      } else {
        await post("/api/declaration-system/my-debts/unit", {}, payload);
      }

      navigate("/my-debts", {
        replace: true,
        state: {
          activeTab: "claims",
          successMessage: isEditMode
            ? "تم تعديل الطلب بنجاح"
            : "تم إنشاء الطلب بنجاح",
        },
      });
    } catch (error) {
      console.error(error);

      setBackendError(
        getBackendErrorMessages(
          error,
          isEditMode
            ? "حدث خطأ أثناء تعديل الطلب"
            : "حدث خطأ أثناء إنشاء الطلب",
        ),
      );
      window.scrollTo(0, 0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/my-debts", {
      replace: true,
      state: { activeTab: "payments" },
    });
  };

  return (
    <MainContent>
      <section className={styleData.listData}>
        <div className="container pt-4 pb-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div className="d-flex align-items-center">
              <Link
                className={`${styleAddNewTaxDeclaration["btn-arrow"]} ms-3`}
                to={"/my-debts"}
                replace
                state={{ activeTab: "payments" }}
              >
                <i className="fa-solid fa-arrow-right mt-1"></i>
              </Link>
              <h3 className="box-title mb-0 ms-2">
                {isEditMode
                  ? "تعديل طلب سداد عن عقار"
                  : "إنشاء طلب سداد عن عقار"}
              </h3>
            </div>
          </div>
          <div className={styleData.box}>
            {lookupError && (
              <div className="alert alert-danger">{lookupError}</div>
            )}
            {backendError && (
              <div className="alert alert-danger" role="alert">
                <ul className="mb-0">
                  {backendError.map((message, index) => (
                    <li key={`${message}-${index}`}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
            {submitInfo && <div className="alert alert-info">{submitInfo}</div>}

            <form onSubmit={handleSubmit} noValidate>
              <div className="row">
                <div className="col-lg-4 col-sm-6">
                  <SearchSelectElement
                    mb4="mb-2"
                    labelHtmlFor="property_type_id"
                    labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                    labelText="نوع العقار"
                    selectClassName={`${stylesRequestData["form-select"]} form-select`}
                    selectID="property_type_id"
                    inputId="property_type_id_input"
                    name="property_type_id"
                    options={propertyTypeOptions}
                    default="أختار نوع العقار.."
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    pText={errors.property_type_id}
                    value={form.property_type_id}
                    onChange={handleSelectChange}
                    isClearable={true}
                    // disabled={isLoading}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <SearchSelectElement
                    mb4="mb-2"
                    labelHtmlFor="governorate_id"
                    labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                    labelText="المحافظة"
                    selectClassName={`${stylesRequestData["form-select"]} form-select`}
                    selectID="governorate_id"
                    inputId="governorate_id_input"
                    name="governorate_id"
                    options={governorateOptions}
                    default="أختار المحافظة.."
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    pText={errors.governorate_id}
                    value={form.governorate_id}
                    onChange={handleSelectChange}
                    isClearable={true}
                    // disabled={isLoading}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <SearchSelectElement
                    mb4="mb-2"
                    labelHtmlFor="police_station_id"
                    labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                    labelText="قسم/مركز الشرطة"
                    selectClassName={`${stylesRequestData["form-select"]} form-select`}
                    selectID="police_station_id"
                    inputId="police_station_id_input"
                    name="police_station_id"
                    options={form.governorate_id ? policeStationOptions : []}
                    default={"أختار مركز/ قسم شرطة .."}
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    pText={errors.police_station_id}
                    value={form.police_station_id}
                    onChange={handleSelectChange}
                    isClearable={true}
                    // disabled={!form.governorate_id}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <InputElement
                    mb4="mb-2"
                    labelHtmlFor="city"
                    labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                    labelText="المدينة"
                    inputType="text"
                    maxLength={100}
                    inputClassName={`${stylesRequestData["form-control"]} form-control`}
                    inputID="city_input"
                    name="city"
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    value={form.city}
                    allowedPattern={GENERAL_TEXT_PATTERN}
                    onChange={handleInputElementChange}
                    pText={errors.city}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <InputElement
                    mb4="mb-2"
                    labelHtmlFor="district"
                    labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                    labelText="الحي / المنطقة"
                    inputType="text"
                    maxLength={100}
                    inputClassName={`${stylesRequestData["form-control"]} form-control`}
                    inputID="district_input"
                    name="district"
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    value={form.district}
                    allowedPattern={GENERAL_TEXT_PATTERN}
                    onChange={handleInputElementChange}
                    pText={errors.district}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <InputElement
                    mb4="mb-2"
                    labelHtmlFor="village"
                    labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                    labelText="القرية"
                    inputType="text"
                    maxLength={100}
                    inputClassName={`${stylesRequestData["form-control"]} form-control`}
                    inputID="village_input"
                    name="village"
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    value={form.village}
                    allowedPattern={GENERAL_TEXT_PATTERN}
                    onChange={handleInputElementChange}
                    pText={errors.village}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <InputElement
                    mb4="mb-2"
                    labelHtmlFor="mogawra"
                    labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                    labelText="المجاورة / المجموعة / المرحلة"
                    inputType="text"
                    maxLength={100}
                    inputClassName={`${stylesRequestData["form-control"]} form-control`}
                    inputID="mogawra_input"
                    name="mogawra"
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    value={form.mogawra}
                    allowedPattern={GENERAL_TEXT_PATTERN}
                    onChange={handleInputElementChange}
                    pText={errors.mogawra}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <InputElement
                    mb4="mb-2"
                    labelHtmlFor="street"
                    labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                    labelText="الشارع"
                    inputType="text"
                    maxLength={100}
                    inputClassName={`${stylesRequestData["form-control"]} form-control`}
                    inputID="street_input"
                    name="street"
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    value={form.street}
                    allowedPattern={GENERAL_TEXT_PATTERN}
                    onChange={handleInputElementChange}
                    pText={errors.street}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <InputElement
                    mb4="mb-2"
                    labelHtmlFor="settlement"
                    labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                    labelText="التجمع السكني"
                    inputType="text"
                    maxLength={100}
                    inputClassName={`${stylesRequestData["form-control"]} form-control`}
                    inputID="settlement_input"
                    name="settlement"
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    value={form.settlement}
                    allowedPattern={GENERAL_TEXT_PATTERN}
                    onChange={handleInputElementChange}
                    pText={errors.settlement}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <InputElement
                    mb4="mb-2"
                    labelHtmlFor="feature_number"
                    labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                    labelText="رقم العقار/المبنى كما هو متعارف عليه"
                    inputType="text"
                    maxLength={100}
                    inputClassName={`${stylesRequestData["form-control"]} form-control`}
                    inputID="feature_number_input"
                    name="feature_number"
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    value={form.feature_number}
                    allowedPattern={GENERAL_TEXT_PATTERN}
                    onChange={handleInputElementChange}
                    pText={errors.feature_number}
                  />
                </div>

                <div className="col-lg-4 col-sm-6">
                  <InputElement
                    mb4="mb-2"
                    labelHtmlFor="national_property_id"
                    labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                    labelText="الرقم القومي العقاري"
                    inputType="text"
                    maxLength={30}
                    inputClassName={`${stylesRequestData["form-control"]} form-control`}
                    inputID="national_property_id_input"
                    name="national_property_id"
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    value={form.national_property_id}
                    allowedPattern={NATIONAL_PROPERTY_ID_PATTERN}
                    onChange={handleInputElementChange}
                    pText={errors.national_property_id}
                    popover={{
                      content:
                        " يمكن ادخال الرقم القومي العقاري من خلال الرقم المدون على ايصال الكهرباء أو الغاز الطبيعي مع مراعاة إرفاق صورة من الإيصال ضمن المستندات",
                    }}
                  />
                </div>

                {isFloorRequired && (
                  <>
                    <div className="col-lg-4 col-sm-6">
                      <SearchSelectElement
                        mb4="mb-2"
                        labelHtmlFor="declaration_floor_id"
                        labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                        labelText="رقم الدور"
                        selectClassName={`${stylesRequestData["form-select"]} form-select`}
                        selectID="declaration_floor_id"
                        inputId="declaration_floor_id_input"
                        name="declaration_floor_id"
                        options={floorOptions}
                        default="أختار رقم الدور.."
                        pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                        pText={errors.declaration_floor_id}
                        value={form.declaration_floor_id}
                        onChange={handleSelectChange}
                        isClearable={true}
                        disabled={false}
                        type="string"
                      />
                    </div>

                    {isOtherFloorSelected && (
                      <div className="col-lg-4 col-sm-6">
                        <InputElement
                          mb4="mb-2"
                          labelHtmlFor="other_floor_number"
                          labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                          labelText="رقم الدور الآخر"
                          inputType="text"
                          maxLength={100}
                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                          inputID="other_floor_number"
                          name="other_floor_number"
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          value={form.other_floor_number}
                          allowedPattern={EXTENDED_TEXT_PATTERN}
                          onChange={handleInputElementChange}
                          pText={errors.other_floor_number}
                        />
                      </div>
                    )}
                  </>
                )}

                {isUnitNumberRequired && (
                  <div className="col-lg-4 col-sm-6">
                    <InputElement
                      mb4="mb-2"
                      labelHtmlFor="unit_number"
                      labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                      labelText="رقم الوحدة"
                      inputType="text"
                      maxLength={100}
                      inputClassName={`${stylesRequestData["form-control"]} form-control`}
                      inputID="unit_number_input"
                      name="unit_number"
                      pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                      value={form.unit_number}
                      allowedPattern={UNIT_TEXT_PATTERN}
                      onChange={handleInputElementChange}
                      pText={errors.unit_number}
                    />
                  </div>
                )}

                <div className="col-lg-4 col-sm-6">
                  <SearchSelectElement
                    mb4="mb-2"
                    labelHtmlFor="payment_reason_id"
                    labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                    labelText="سبب السداد"
                    selectClassName={`${stylesRequestData["form-select"]} form-select`}
                    selectID="payment_reason_id"
                    inputId="payment_reason_id_input"
                    name="payment_reason_id"
                    options={paymentReasons}
                    default="أختار سبب السداد.."
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    pText={errors.payment_reason_id}
                    value={form.payment_reason_id}
                    onChange={handleSelectChange}
                    isClearable={true}
                    type="string"
                  />
                </div>

                {isOtherPaymentReasonSelected && (
                  <div className="col-lg-4 col-sm-6">
                    <InputElement
                      mb4="mb-2"
                      labelHtmlFor="other_payment_reason"
                      labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                      labelText="السبب الآخر"
                      inputType="text"
                      maxLength={100}
                      inputClassName={`${stylesRequestData["form-control"]} form-control`}
                      inputID="other_payment_reason_input"
                      name="other_payment_reason"
                      pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                      value={form.other_payment_reason}
                      allowedPattern={UNIT_TEXT_PATTERN}
                      onChange={handleInputElementChange}
                      pText={errors.other_payment_reason}
                    />
                  </div>
                )}

                <div className="col-lg-4 col-sm-6">
                  <InputElement
                    mb4="mb-2"
                    labelHtmlFor="amount"
                    labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                    labelText="المبلغ المطلوب سداده"
                    inputType="text"
                    inputClassName={`${stylesRequestData["form-control"]} form-control`}
                    inputID="amount_input"
                    name="amount"
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    value={form.amount}
                    allowedPattern={AMOUNT_TYPING_PATTERN}
                    onChange={handleAmountInputChange}
                    pText={errors.amount}
                    placeholder="0.00"
                  />
                </div>

                {/* <div className="col-12">
                  <label
                    className={`${stylesRequestData["list-group-item"]} list-group-item border-0 mb-3 mt-3 d-inline-flex align-items-center`}
                    htmlFor="pay_from_wallet"
                  >
                    <input
                      id="pay_from_wallet"
                      className={`${stylesRequestData["form-check-input"]} form-check-input ms-3 mt-1`}
                      type="checkbox"
                      checked={form.pay_from_wallet}
                      disabled={Number(walletBalance.balance || 0) <= 0}
                      onChange={(event) =>
                        setField("pay_from_wallet", event.target.checked)
                      }
                    />
                    <span
                      className="form-check-label"
                      style={{ fontSize: "20px", fontWeight: "bold" }}
                    >
                      السداد من حساب التسويات
                    </span>
                  </label>
                </div> */}

                <div className="col-lg-4 col-sm-6">
                  <SearchSelectElement
                    mb4="mb-2"
                    labelHtmlFor="has_debt_docs"
                    labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
                    labelText="هل توجد مستندات دالة على المديونية؟"
                    selectClassName={`${stylesRequestData["form-select"]} form-select`}
                    selectID="has_debt_docs"
                    inputId="has_debt_docs_input"
                    name="has_debt_docs"
                    options={hasDocsOptions}
                    default="أختار نعم أو لا.."
                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                    pText={errors.has_debt_docs}
                    value={form.has_debt_docs}
                    onChange={handleSelectChange}
                    isClearable={true}
                    type="string"
                  />
                </div>
              </div>

              {shouldShowDocsSection && (
                <div className={`mt-4`}>
                  {documentRows.map((row, index) => (
                    <div className="row" key={row.id}>
                      <div className="col-12 col-md-4">
                        <InputElement
                          value={row.document_name || ""}
                          name="document_name"
                          onChange={(_parent, _component, _name, value) => {
                            handleDocumentNameChange(row.id, value);
                          }}
                          mb4="mb-4"
                          labelHtmlFor={`document_name_${row.id}`}
                          labelClassName={`required ${stylesRequestData["form-label"]} form-label`}
                          labelText="اسم المستند"
                          inputType="text"
                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                          inputID={`document_name_input_${row.id}`}
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText={errors[`document_name_${index}`] || ""}
                          allowedPattern={UNIT_TEXT_PATTERN}
                          maxLength={100}
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <InputElement
                          key={`${inputKeys[row.id] || 0}-${row.path || "new"}`}
                          fileInputRef={documentFileInputRef.current[row.id]}
                          labelHtmlFor={`document_file_${row.id}`}
                          labelClassName={`required ${stylesRequestData["form-label"]} form-label`}
                          labelText={row.path ? "صورة المرفق" : "اختيار ملف"}
                          name="file"
                          inputType="file"
                          fileName={
                            row.path ? row.original_file_name : "إرفاق ملف"
                          }
                          inputID={`document_file_input_${row.id}`}
                          inputClassName={`${stylesRequestData["form-control"]} form-control ${stylesRequestData["fileCustom"]}`}
                          inputIcon={`${stylesRequestData["input-icon"]} input-icon`}
                          icon="fa-solid fa-cloud-arrow-up"
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText={
                            errors[`document_file_${index}`] ||
                            row.file_error ||
                            ""
                          }
                          onChange={(_parent, _component, _name, file) => {
                            if (file) {
                              handleDocumentFileChange(row.id, file);
                            }
                          }}
                          deleteFunction={() =>
                            handleRemoveDocumentFile(row.id)
                          }
                          disabled={!row.document_name?.trim()}
                        />
                      </div>

                      <div className="col-md-4 col-sm-6 row gap-2">
                        <div className="col-md-5 col-sm-6">
                          {documentRows.length > 1 && index !== 0 && (
                            <button
                              type="button"
                              onClick={() => removeDocumentRow(row.id)}
                              className={`${stylesRequestData["btn-delete"]} text-danger`}
                              style={{ marginTop: "33px" }}
                            >
                              حذف المستند
                              <i className="fa-regular fa-trash-can fa-lg ms-1"></i>
                            </button>
                          )}
                        </div>

                        {index === documentRows.length - 1 && (
                          <div className="col-md-5 col-sm-6">
                            <button
                              type="button"
                              onClick={addDocumentRow}
                              className={`${
                                // !row.document_name?.trim() ||
                                // (!row.file && !row.path) ||
                                documentRows.length === 5
                                  ? stylesRequestData["btn-add-disabled"]
                                  : stylesRequestData["btn-add"]
                              }`}
                              style={{ marginTop: "33px" }}
                              disabled={
                                // !row.document_name?.trim() ||
                                // (!row.file && !row.path) ||
                                documentRows.length === 5
                              }
                            >
                              إضافة مستند آخر
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="d-flex flex-lg-row flex-column justify-content-start gap-3 align-items-center pb-5 mt-5">
                <button
                  type="submit"
                  className={`${stylesRequestData["btn-request"]}`}
                  disabled={
                    isUploadingDocuments || backendError.length > 0 || isLoading
                  }
                >
                  {isEditMode ? "حفظ التعديلات" : "إنشاء طلب السداد"}
                </button>
                <button
                  type="button"
                  className={`${stylesRequestData["btn-delete"]}`}
                  onClick={handleCancel}
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </MainContent>
  );
}

export default CreatePropertyPaymentRequest;
